/**
 * Simplified Chart Adjustment Panel
 * 
 * Philosophy: "Smart Defaults, Clear Errors"
 * - Uses intelligent defaults to prevent most invalid combinations
 * - Shows all available fields in dropdowns (simple & predictable)
 * - Lets backend handle validation and provides clear error messages
 * - Reduces complexity by removing frontend validation dependencies
 * 
 * This approach balances user experience with maintainability by:
 * 1. Making dropdowns predictable (all fields always shown)
 * 2. Using smart defaults to guide users toward valid combinations
 * 3. Relying on backend as single source of truth for validation
 * 4. Showing clear error messages when configurations fail
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { appConfig } from '../config/AppConfig';
import { 
  Settings, 
  BarChart3, 
  LineChart, 
  PieChart,
  AreaChart,
  TrendingUp,
  Layers,
  X,
  Check,
  RefreshCw,
  ChevronDown,
  Info
} from 'lucide-react';

const PanelContainer = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid #e2e8f0;
  margin-top: 16px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  color: white;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  color: white;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const PanelContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GroupLabel = styled.div`
  font-weight: 600;
  color: #2d3748;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ChartTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
`;

const ChartTypeButton = styled(motion.button)`
  background: ${props => props.selected ? 'linear-gradient(135deg, #1B8C3C 0%, #006200 100%)' : 'white'};
  color: ${props => props.selected ? 'white' : '#4a5568'};
  border: 2px solid ${props => props.selected ? 'transparent' : '#e2e8f0'};
  border-radius: 8px;
  padding: 12px 8px;
  min-height: 95px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    border-color: ${props => props.selected ? 'transparent' : '#cbd5e0'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const AxisSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Select = styled.select`
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  color: #2d3748;
  cursor: pointer;
  transition: border-color 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 20px;
  padding-right: 36px;

  &:focus {
    outline: none;
    border-color: #1B8C3C;
  }

  option {
    padding: 8px;
    background: white;
    color: #2d3748;
    
    &:checked {
      background: #1B8C3C;
      color: white;
    }
    
    &:hover {
      background: #f7fafc;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
`;

const HelperText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 10px;
  background: #f7fafc;
  border-radius: 6px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  
  svg {
    width: 14px;
    height: 14px;
    color: #94a3b8;
    flex-shrink: 0;
    margin-top: 2px;
  }
  
  .text {
    flex: 1;
  }
  
  .example {
    margin-top: 4px;
    font-style: italic;
    color: #94a3b8;
    font-size: 12px;
  }
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  background: ${props => {
    if (props.variant === 'primary') return 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)';
    if (props.variant === 'secondary') return 'linear-gradient(135deg, #1B8C3C 0%, #006200 100%)';
    return '#f7fafc';
  }};
  color: ${props => props.variant === 'cancel' ? '#4a5568' : 'white'};
  border: ${props => props.variant === 'cancel' ? '2px solid #e2e8f0' : 'none'};
  border-radius: 6px;
  padding: 10px 16px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Chart type configurations
const CHART_TYPES = [
  { value: 'bar', label: 'رسم بياني شريطي', icon: BarChart3, description: 'مقارنة الفئات' },
  { value: 'line', label: 'رسم بياني خطي', icon: LineChart, description: 'إظهار الاتجاهات عبر الزمن' },
  { value: 'pie', label: 'رسم بياني دائري', icon: PieChart, description: 'إظهار النسب' },
  { value: 'area', label: 'رسم بياني مساحي', icon: AreaChart, description: 'إظهار الحجم عبر الزمن' },
  { value: 'grouped_bar', label: 'رسم بياني شريطي مجمع', icon: Layers, description: 'مقارنة بالتقسيم' },
  { value: 'stacked_bar', label: 'رسم بياني شريطي مكدس', icon: TrendingUp, description: 'إظهار التكوين' },
  { value: 'multi_line', label: 'متعدد الخطوط', icon: LineChart, description: 'مقارنة الاتجاهات بالتقسيم' },
];

// Chart-specific field configurations - only for chart types that actually use them
const CHART_FIELD_CONFIG = {
  pie: {
    colorLabel: 'الفئات (مطلوب)',
    colorHelper: 'يحدد ما يمثله كل قطعة من المخطط الدائري. كل قيمة فريدة تصبح قطعة.',
    colorExample: 'مثال: أسماء الفرق، أنواع المنتجات، أو الأقسام'
  },
  grouped_bar: {
    colorLabel: 'تجميع حسب (مطلوب)',
    colorHelper: 'يجمع الأشرطة جنباً إلى جنب للمقارنة المباشرة. مثالي لتحليل التقسيم.',
    colorExample: 'مثال: التجميع حسب السنة لمقارنة القيم الشهرية عبر السنوات'
  },
  stacked_bar: {
    colorLabel: 'تكديس حسب (مطلوب)',
    colorHelper: 'يكدس القيم فوق بعضها لإظهار الإجمالي والتكوين. رائع لتحليل الجزء من الكل.',
    colorExample: 'مثال: التكديس حسب نوع المنتج لإظهار إجمالي المبيعات ومزيج المنتجات'
  },
  multi_line: {
    colorLabel: 'خطوط حسب (مطلوب)',
    colorHelper: 'ينشئ خطاً واحداً لكل قيمة فريدة. مثالي لمقارنة الاتجاهات عبر الفئات.',
    colorExample: 'مثال: خط واحد لكل منتج لمقارنة اتجاهاتها'
  }
};

function ChartAdjustmentPanel({
  isOpen,
  onClose,
  onAdjust,
  isAdjusting = false,
  availableColumns = [],
  currentChart = null,
  messageData = null,  // Add messageData prop for validation context
  chartSpecificData = null,  // Chart-specific data for multi-chart scenarios
  chartSpecificColumns = null,  // Chart-specific columns for multi-chart scenarios
  lastAdjustmentError = null  // Error from last adjustment attempt
}) {
  // Helper function to get appropriate default fields based on chart type and available columns
  const getDefaultFields = (chartType, availableColumns, currentChart) => {
    // For new adjustments, select intelligent defaults from available columns
    // Don't use existing chart values to allow fresh selection

    if (!availableColumns || availableColumns.length === 0) {
      return {
        x_axis: '',
        y_axis: '',
        color: '',
        theta: ''
      };
    }

    // Get actual column types from backend (instead of guessing from names)
    const dtypes = messageData?.visualization?.dtypes || {};

    // Categorize columns by actual type (not name-based heuristics)
    const numericColumns = availableColumns.filter(col => {
      const dtype = dtypes[col];
      return dtype === 'integer' || dtype === 'float';
    });

    const categoricalColumns = availableColumns.filter(col =>
      !numericColumns.includes(col)
    );

    // Select appropriate defaults based on chart type
    if (chartType === 'pie') {
      return {
        x_axis: '',
        y_axis: '',
        color: categoricalColumns[0] || availableColumns[0], // First categorical for grouping
        theta: numericColumns[0] || availableColumns[0]  // First numeric for values
      };
    } else {
      return {
        x_axis: categoricalColumns[0] || availableColumns[0], // First categorical for x-axis
        y_axis: numericColumns[0] || availableColumns[1] || availableColumns[0], // First numeric for y-axis
        color: '',  // None - optional for grouping
        theta: ''
      };
    }
  };

  const [selectedType, setSelectedType] = useState(currentChart?.chart_type || 'bar');
  
  // Simple error state for when backend rejects the configuration
  const [lastError, setLastError] = useState(null);

  // State for chart compatibility filtering
  const [compatibleTypes, setCompatibleTypes] = useState([]);
  const [compatibilityDetails, setCompatibilityDetails] = useState({}); // Store detailed reasons
  const [loadingCompatibility, setLoadingCompatibility] = useState(false);
  const [compatibilityError, setCompatibilityError] = useState(null);

  // State for field filtering based on data types
  const [validFields, setValidFields] = useState({
    x_axis: [],
    y_axis: [],
    color: [],
    theta: []
  });
  const [loadingValidFields, setLoadingValidFields] = useState(false);

  // Helper to build field options filtered by data type compatibility
  const buildFieldOptions = (encoding, isOptional = false) => {
    const options = [];

    // Add optional "None" for optional fields
    if (isOptional) {
      options.push(<option key="none" value="">None</option>);
    }

    // Use filtered valid fields if available, otherwise show all columns
    const fieldsToShow = validFields[encoding] && validFields[encoding].length > 0
      ? validFields[encoding]
      : availableColumns;

    // Add field options
    fieldsToShow.forEach(col => {
      options.push(<option key={col} value={col}>{col}</option>);
    });

    return options;
  };

  // Helper to create field change handlers that clear errors
  const createFieldChangeHandler = (setter) => {
    return (e) => {
      setLastError(null); // Clear error when user makes changes
      setter(e.target.value);
    };
  };

  // Function to fetch valid fields for selected chart type
  const fetchValidFields = async (chartType) => {
    if (!messageData || !messageData.results) {
      console.warn('No message data available for field validation');
      // Fallback: show all columns if no data
      setValidFields({
        x_axis: availableColumns,
        y_axis: availableColumns,
        color: availableColumns,
        theta: availableColumns
      });
      return;
    }

    setLoadingValidFields(true);

    try {
      console.log('Fetching valid fields for chart type:', chartType);

      await appConfig.load();
      const chartApiUrl = appConfig.getChartApiUrl();
      const response = await fetch(`${chartApiUrl}/validate-fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chart_type: chartType,
          data: {
            columns: availableColumns,
            rows: messageData.results || []
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Valid fields response:', data);

      if (data.valid_fields) {
        setValidFields(data.valid_fields);

        // Auto-adjust selected fields if they're no longer valid
        const adjustFieldIfInvalid = (currentValue, validList, setter) => {
          if (currentValue && !validList.includes(currentValue)) {
            console.log(`Field '${currentValue}' is not valid for ${chartType}, auto-selecting first valid option`);
            setter(validList[0] || '');
          }
        };

        if (chartType === 'pie') {
          adjustFieldIfInvalid(theta, data.valid_fields.theta, setTheta);
          adjustFieldIfInvalid(colorField, data.valid_fields.color, setColorField);
        } else {
          adjustFieldIfInvalid(xAxis, data.valid_fields.x_axis, setXAxis);
          adjustFieldIfInvalid(yAxis, data.valid_fields.y_axis, setYAxis);
          if (['grouped_bar', 'stacked_bar', 'multi_line'].includes(chartType)) {
            adjustFieldIfInvalid(colorField, data.valid_fields.color, setColorField);
          }
        }
      }

    } catch (error) {
      console.error('Failed to fetch valid fields:', error);
      // Fallback: show all columns on error
      setValidFields({
        x_axis: availableColumns,
        y_axis: availableColumns,
        color: availableColumns,
        theta: availableColumns
      });
    } finally {
      setLoadingValidFields(false);
    }
  };

  // Function to fetch compatible chart types
  const fetchCompatibleChartTypes = async () => {
    // Use chart-specific data if available (multi-chart scenario), otherwise use message data
    const dataToCheck = chartSpecificData || messageData?.results;
    const columnsToCheck = chartSpecificColumns || availableColumns;

    if (!dataToCheck) {
      console.warn('No data available for compatibility analysis');
      setCompatibleTypes(CHART_TYPES.map(ct => ct.value)); // Fallback to all types
      return;
    }

    setLoadingCompatibility(true);
    setCompatibilityError(null);

    try {
      console.log('🔍 Fetching compatible chart types for data:', {
        isChartSpecific: !!chartSpecificData,
        chartIndex: currentChart?.chart_index,
        columns: columnsToCheck,
        results: dataToCheck?.length,
        sampleData: dataToCheck?.slice(0, 3) // Show first 3 rows
      });

      await appConfig.load();
      const mainApiUrl = appConfig.getMainApiUrl();
      const response = await fetch(`${mainApiUrl}/get-compatible-chart-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          column_names: columnsToCheck,
          results: dataToCheck || [],
          sql_query: messageData?.sql_query || ''
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Chart compatibility analysis result:', data);

      // Extract compatibility details
      const allChartTypes = data.compatible_chart_types || [];
      const compatible = allChartTypes
        .filter(ct => ct.requirements_met)
        .map(ct => ct.type);

      // Store detailed reasons for each chart type
      const details = {};
      allChartTypes.forEach(ct => {
        details[ct.type] = {
          compatible: ct.requirements_met,
          reason: ct.reason,
          confidence: ct.confidence
        };
      });

      // If no compatible types, fall back to all types
      const finalCompatibleTypes = compatible.length > 0 
        ? compatible 
        : CHART_TYPES.map(ct => ct.value);

      setCompatibleTypes(finalCompatibleTypes);
      setCompatibilityDetails(details);
      console.log('Compatible chart types:', finalCompatibleTypes);
      console.log('Compatibility details:', details);

      // Auto-switch to first compatible chart type if current selection is not compatible
      if (finalCompatibleTypes.length > 0 && !finalCompatibleTypes.includes(selectedType)) {
        const firstCompatible = finalCompatibleTypes[0];
        console.log(`Current chart type '${selectedType}' is not compatible. Auto-switching to '${firstCompatible}'.`);
        
        // Just change the chart type without resetting user's field selections
        setSelectedType(firstCompatible);
      }

    } catch (error) {
      console.error('Failed to fetch compatible chart types:', error);
      setCompatibilityError(error.message);
      // Fallback to all chart types on error
      setCompatibleTypes(CHART_TYPES.map(ct => ct.value));
    } finally {
      setLoadingCompatibility(false);
    }
  };
  
  // Get initial field values
  const initialFields = getDefaultFields(selectedType, availableColumns, currentChart);
  const [xAxis, setXAxis] = useState(initialFields.x_axis);
  const [yAxis, setYAxis] = useState(initialFields.y_axis);
  const [theta, setTheta] = useState(initialFields.theta);
  const [colorField, setColorField] = useState(initialFields.color);

  // Effect to sync selectedType with currentChart changes
  React.useEffect(() => {
    const chartType = currentChart?.chart_type || 'bar';
    setSelectedType(chartType);
  }, [currentChart?.chart_type]);

  // Effect to reset state when panel opens
  React.useEffect(() => {
    if (isOpen) {
      // Clear any previous error
      setLastError(null);
      
      // Reset fields when panel opens with fresh defaults (only if empty)
      const chartType = currentChart?.chart_type || 'bar';
      const newFields = getDefaultFields(chartType, availableColumns, currentChart);
      
      // selectedType is already synced by the effect above
      // Only set defaults if fields are currently empty - preserve user selections
      if (!xAxis) setXAxis(newFields.x_axis);
      if (!yAxis) setYAxis(newFields.y_axis);
      if (!theta) setTheta(newFields.theta);
      if (!colorField) setColorField(newFields.color);
    }
  }, [isOpen, availableColumns]);

  // Effect to fetch compatible chart types when panel opens
  React.useEffect(() => {
    if (isOpen && availableColumns.length > 0) {
      fetchCompatibleChartTypes();
    }
  }, [isOpen, availableColumns, messageData, chartSpecificData, chartSpecificColumns]);

  // Effect to fetch valid fields when chart type changes
  React.useEffect(() => {
    if (isOpen && selectedType && availableColumns.length > 0) {
      fetchValidFields(selectedType);
    }
  }, [isOpen, selectedType, availableColumns, messageData]);

  // Effect to handle external error from parent component
  React.useEffect(() => {
    if (lastAdjustmentError) {
      setLastError(lastAdjustmentError);
    }
  }, [lastAdjustmentError]);

  // Effect to detect when adjustment completes
  React.useEffect(() => {
    // When isAdjusting changes from true to false, adjustment is complete
    // We no longer auto-close to let users see the results and decide what to do next
    // This prevents the "disappearing panel" issue while still clearing error states
    if (!isAdjusting) {
      // Just log for debugging - no auto-close
      console.log('Chart adjustment completed');
    }
  }, [isAdjusting]);

  // Update fields when chart type changes
  const handleChartTypeChange = (newType) => {
    // Clear any previous errors when changing chart type
    setLastError(null);
    
    setSelectedType(newType);
    
    // Auto-populate fields for the new chart type
    const newFields = getDefaultFields(newType, availableColumns, currentChart);
    
    if (newType === 'pie') {
      // For pie charts, reset x/y and set theta/color
      setXAxis('');
      setYAxis('');
      setTheta(newFields.theta);
      setColorField(newFields.color);
    } else {
      // For other charts, ensure we have x/y axes
      // Only use defaults if fields are empty - preserve user selections
      if (!xAxis) setXAxis(newFields.x_axis);
      if (!yAxis) setYAxis(newFields.y_axis);
      setTheta(''); // Clear theta for non-pie charts
      
      // Handle color field based on chart type requirements
      const simpleChartTypes = ['bar', 'line', 'area'];
      if (simpleChartTypes.includes(newType)) {
        // Simple charts don't support color grouping - always clear it
        setColorField('');
      } else {
        // Grouped charts support color - keep existing or use default
        if (!colorField) {
          setColorField(newFields.color);
        }
      }
    }
  };

  const handleAdjust = () => {
    // Prevent multiple clicks
    if (isAdjusting) return;

    // Clear any previous error when starting new adjustment
    setLastError(null);

    const adjustmentOptions = {
      chart_type: selectedType,
      chart_index: currentChart?.chart_index || 0  // Include chart index for multi-chart scenarios
    };

    // Helper to clean field values - empty string or "None" becomes null
    const cleanField = (value) => {
      if (!value || value === '' || value === 'None') return null;
      return value;
    };

    // For pie charts, use theta instead of x/y axis
    if (selectedType === 'pie') {
      adjustmentOptions.theta = cleanField(theta);
      adjustmentOptions.color = cleanField(colorField);
      // Don't include x_axis and y_axis for pie charts
    } else {
      adjustmentOptions.x_axis = cleanField(xAxis);
      adjustmentOptions.y_axis = cleanField(yAxis);
      adjustmentOptions.color = cleanField(colorField);
      // Don't include theta for non-pie charts
    }

    // Debug what's being sent
    console.log('🔧 Chart adjustment request:', {
      selectedType,
      xAxis,
      yAxis,
      colorField,
      theta,
      adjustmentOptions,
      availableColumns,
      chart_index: adjustmentOptions.chart_index  // Show which chart is being adjusted
    });

    // Call onAdjust and let backend handle validation
    onAdjust(adjustmentOptions);
  };

  // Simple check: chart type is selected and we have available columns
  const canApply = selectedType && availableColumns && availableColumns.length > 0;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <PanelContainer
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        <PanelHeader>
          <HeaderTitle>
            <Settings />
            تعديل الرسم البياني
          </HeaderTitle>
          <CloseButton onClick={onClose}>
            <X />
          </CloseButton>
        </PanelHeader>

        <PanelContent>
          {/* Chart Type Selection */}
          <OptionGroup>
            <GroupLabel>
              نوع الرسم البياني
              {loadingCompatibility && (
                <span style={{
                  marginLeft: '8px',
                  fontSize: '11px',
                  color: '#64748b',
                  fontWeight: 'normal'
                }}>
                  (جاري تحليل التوافق...)
                </span>
              )}
            </GroupLabel>
            
            {/* Compatibility error message */}
            {compatibilityError && (
              <div style={{
                padding: '8px 12px',
                background: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#856404',
                marginBottom: '8px'
              }}>
                ⚠️ لم يتمكن من تحليل توافق الرسم البياني: {compatibilityError}
                <br />
                <span style={{ fontSize: '11px', color: '#6c757d' }}>
                  يتم عرض جميع أنواع الرسوم البيانية كبديل.
                </span>
              </div>
            )}
            
            <ChartTypeGrid>
              {CHART_TYPES.map((type) => {
                const IconComponent = type.icon;
                const isCompatible = compatibleTypes.length === 0 || compatibleTypes.includes(type.value);
                const isLoading = loadingCompatibility;
                const details = compatibilityDetails[type.value];
                
                // Create helpful tooltip messages
                const getTooltipMessage = () => {
                  if (isLoading) return 'جاري تحليل توافق الرسم البياني...';

                  if (details) {
                    if (details.compatible) {
                      return `${details.reason}`; // Removed green checkmark for compatible charts
                    } else {
                      return `❌ ${details.reason}`;
                    }
                  }

                  // Fallback messages for when no details available
                  if (!isCompatible) {
                    const fallbackMessages = {
                      'line': 'يتطلب بيانات سلسلة زمنية أو أعمدة رقمية متعددة',
                      'area': 'يتطلب بيانات سلسلة زمنية أو أعمدة رقمية متعددة',
                      'pie': 'يتطلب بيانات فئوية للتجميع',
                      'grouped_bar': 'يتطلب حقول فئوية متعددة للتجميع',
                      'stacked_bar': 'يتطلب حقول فئوية متعددة للتكديس'
                    };
                    return `❌ ${fallbackMessages[type.value] || 'غير متوافق مع هيكل البيانات الحالي'}`;
                  }

                  return '';
                };
                
                return (
                  <ChartTypeButton
                    key={type.value}
                    selected={selectedType === type.value}
                    onClick={() => isCompatible && handleChartTypeChange(type.value)}
                    disabled={!isCompatible || isLoading}
                    style={{ 
                      opacity: isLoading ? 0.6 : (isCompatible ? 1 : 0.4),
                      cursor: isLoading ? 'wait' : (isCompatible ? 'pointer' : 'not-allowed'),
                      transform: isLoading ? 'none' : undefined
                    }}
                    title={getTooltipMessage()}
                    whileHover={isCompatible && !isLoading ? { scale: 1.02 } : {}}
                    whileTap={isCompatible && !isLoading ? { scale: 0.98 } : {}}
                  >
                    <IconComponent />
                    <div style={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      <span style={{ fontWeight: '600' }}>{type.label}</span>
                      <span style={{ 
                        fontSize: '9px', 
                        opacity: 0.8,
                        lineHeight: '1.1'
                      }}>
                        {type.description}
                      </span>
                    </div>
                    {!isCompatible && !isLoading && (
                      <span style={{ 
                        fontSize: '10px', 
                        color: '#ef4444',
                        marginLeft: '4px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        ⚠️
                      </span>
                    )}
                    
                    {/* Show explanation text for disabled charts */}
                    {!isCompatible && !isLoading && (
                      <div style={{
                        fontSize: '10px',
                        color: '#6b7280',
                        marginTop: '4px',
                        lineHeight: '1.2',
                        textAlign: 'center',
                        fontWeight: 'normal'
                      }}>
                        {details?.reason || (
                          type.value === 'line' ? 'يحتاج بيانات زمنية' :
                          type.value === 'area' ? 'يحتاج بيانات زمنية' :
                          type.value === 'grouped_bar' ? 'يحتاج حقل تجميع' :
                          type.value === 'stacked_bar' ? 'يحتاج حقل تكديس' :
                          'غير متوافق'
                        )}
                      </div>
                    )}
                    
                    {/* Show success indicator for compatible charts */}
                  </ChartTypeButton>
                );
              })}
            </ChartTypeGrid>
          </OptionGroup>

          {/* Axis Configuration */}
          {availableColumns.length > 0 && (
            <>
              {selectedType === 'pie' ? (
                <>
                  {/* Pie Chart Configuration */}
                  <OptionGroup>
                    <GroupLabel>القيمة</GroupLabel>
                    <AxisSelector>
                      <Select
                        value={theta}
                        onChange={createFieldChangeHandler(setTheta)}
                      >
                        {buildFieldOptions('theta', false)}
                      </Select>
                    </AxisSelector>
                  </OptionGroup>

                  <OptionGroup>
                    <GroupLabel>{CHART_FIELD_CONFIG.pie?.colorLabel || 'الفئات'}</GroupLabel>
                    <AxisSelector>
                      <Select 
                        value={colorField} 
                        onChange={createFieldChangeHandler(setColorField)}
                      >
                        {buildFieldOptions('color', true)}
                      </Select>
                    </AxisSelector>
                    <HelperText>
                      <Info />
                      <div className="text">
                        {CHART_FIELD_CONFIG.pie?.colorHelper}
                        <div className="example">{CHART_FIELD_CONFIG.pie?.colorExample}</div>
                      </div>
                    </HelperText>
                  </OptionGroup>
                </>
              ) : (
                <>
                  {/* Regular Chart Configuration */}
                  <OptionGroup>
                    <GroupLabel>المحور السيني</GroupLabel>
                    <AxisSelector>
                      <Select
                        value={xAxis}
                        onChange={createFieldChangeHandler(setXAxis)}
                      >
                        {buildFieldOptions('x_axis', false)}
                      </Select>
                    </AxisSelector>
                  </OptionGroup>

                  <OptionGroup>
                    <GroupLabel>المحور الصادي</GroupLabel>
                    <AxisSelector>
                      <Select
                        value={yAxis}
                        onChange={createFieldChangeHandler(setYAxis)}
                      >
                        {buildFieldOptions('y_axis', false)}
                      </Select>
                    </AxisSelector>
                  </OptionGroup>

                  {/* Only show color field for chart types that support it */}
                  {['grouped_bar', 'stacked_bar', 'multi_line'].includes(selectedType) && (
                    <OptionGroup>
                      <GroupLabel>{CHART_FIELD_CONFIG[selectedType]?.colorLabel || 'تقسيم حسب'}</GroupLabel>
                      <AxisSelector>
                        <Select 
                          value={colorField} 
                          onChange={createFieldChangeHandler(setColorField)}
                        >
                          {buildFieldOptions('color', true)}
                        </Select>
                      </AxisSelector>
                      <HelperText>
                        <Info />
                        <div className="text">
                          {CHART_FIELD_CONFIG[selectedType]?.colorHelper || 'اختر حقلاً لتقسيم أو تجميع البيانات.'}
                          <div className="example">{CHART_FIELD_CONFIG[selectedType]?.colorExample}</div>
                        </div>
                      </HelperText>
                    </OptionGroup>
                  )}
                </>
              )}
            </>
          )}

          {/* Error Messages */}
          {lastError && (
            <OptionGroup>
              <div style={{
                padding: '12px',
                background: '#fef2f2',
                borderRadius: '8px',
                color: '#dc2626',
                fontSize: '14px',
                border: '1px solid #fecaca'
              }}>
                ⚠️ فشل تعديل الرسم البياني
                <div style={{ marginTop: '4px', fontSize: '13px' }}>
                  {lastError}
                </div>
              </div>
            </OptionGroup>
          )}

          {/* Action Buttons */}
          <ActionButtons>
            <ActionButton
              variant="cancel"
              onClick={onClose}
              disabled={isAdjusting}
            >
              إلغاء
            </ActionButton>

            <ActionButton
              variant="primary"
              onClick={handleAdjust}
              disabled={!canApply || isAdjusting}
            >
              {isAdjusting ? (
                <>
                  <LoadingSpinner />
                  جاري التعديل...
                </>
              ) : (
                <>
                  <Check />
                  تطبيق التغييرات
                </>
              )}
            </ActionButton>
          </ActionButtons>
        </PanelContent>
      </PanelContainer>
    </AnimatePresence>
  );
}

export default ChartAdjustmentPanel;